/**
 * Pure-logic core for the widget action governance pipeline.
 *
 * Lives in a separate file from `use-widget-action.ts` so the unit
 * tests (`node --test`) can import it without dragging React +
 * `@tyndo/hooks` into the runtime resolution graph. The React hook
 * in `use-widget-action.ts` composes this function with the
 * authenticated `useApiClient` and React state.
 *
 * Flow:
 *
 *   1. If `action.confirm` is set → prompt; abort on cancel.
 *   2. Generate a fresh UUID for the Idempotency-Key header.
 *   3. POST to the gateway's invoke endpoint.
 *   4. On success → onSuccess. On error → optional rollback + onError + rethrow.
 */

import type { WidgetAction } from '@tyndo/types';

// ────────────────────────────────────────────────────────────────────────
// Public shapes.
// ────────────────────────────────────────────────────────────────────────

export interface WidgetActionInvokeResult {
  /** "success" | "denied" | "error". */
  outcome: string;
  /** Audit row id. Surfaced in the result toast so the audit drawer can deep-link. */
  auditId: string;
  /** True when the gateway returned a cached replay for the same idempotency key. */
  idempotentReplay: boolean;
  /** Human-readable message — passed through to the toast. */
  message: string;
}

/**
 * Dependencies handed in by the React hook (or the test harness). Every
 * IO touchpoint is a function so the test can swap in stubs.
 */
export interface WidgetActionInvocationDeps {
  /** UUID v4 generator. Production passes `crypto.randomUUID`. */
  generateUuid: () => string;
  /**
   * Confirm prompt. Production drives the shared `ConfirmActionDialog` (see
   * `use-widget-action.tsx`) and awaits the operator's decision; tests pass a
   * synchronous stub. Return `false` when the user cancels — the invocation
   * aborts before any network call fires.
   */
  confirm?: (message: string) => boolean | Promise<boolean>;
  /** Issues the POST. Returns the parsed response body or throws on non-2xx. */
  post: (url: string, body: unknown, headers: Record<string, string>) => Promise<WidgetActionInvokeResult>;
  onSuccess?: (result: WidgetActionInvokeResult) => void;
  onError?: (error: Error) => void;
  /**
   * Optimistic rollback. Fired when the POST throws AND the action
   * declares `optimistic.rollbackOnError`.
   */
  onRollback?: () => void;
}

export interface WidgetActionInvocationInput {
  instanceId: string;
  action: WidgetAction;
  /** User-supplied parameters — passed straight through as the body. */
  parameters: unknown;
}

// ────────────────────────────────────────────────────────────────────────
// Flow.
// ────────────────────────────────────────────────────────────────────────

/**
 * One-shot invocation flow. Used by the React hook AND the unit tests.
 *
 * Returns the parsed result OR `null` when the user cancels the confirm
 * dialog. Errors propagate to `onError` and are re-thrown so the caller
 * can also observe them.
 */
export async function runWidgetActionInvocation(
  input: WidgetActionInvocationInput,
  deps: WidgetActionInvocationDeps,
): Promise<WidgetActionInvokeResult | null> {
  const { instanceId, action, parameters } = input;

  // 1. Confirm gate — bail before we generate a key or hit the network.
  if (action.confirm && deps.confirm) {
    const ok = await deps.confirm(`${action.confirm.title}\n\n${action.confirm.body}`);
    if (!ok) return null;
  }

  // 2. Fresh idempotency key per click.
  const idempotencyKey = deps.generateUuid();

  // 3. POST through the gateway. The endpoint shape lines up with
  //    WidgetsModule.ConfigureEndpoints — see the .NET side.
  const endpoint = `/api/v1/widgets/instances/${instanceId}/actions/${action.actionId}/invoke`;
  try {
    const result = await deps.post(endpoint, parameters ?? {}, {
      'Idempotency-Key': idempotencyKey,
    });
    deps.onSuccess?.(result);
    return result;
  } catch (rawError) {
    const error = rawError instanceof Error ? rawError : new Error(String(rawError));
    // 4. Optimistic rollback fires before onError so the UI snaps back
    //    in the same render the error toast appears in.
    if (action.optimistic?.rollbackOnError) {
      deps.onRollback?.();
    }
    deps.onError?.(error);
    throw error;
  }
}

/**
 * crypto.randomUUID polyfill — falls back to a v4-ish generator on
 * browsers/test runtimes without `crypto.randomUUID`.
 */
export function safeUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Minimal v4 fallback for old runtimes / SSR.
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
